/**
 * PROGÈRE — Configuration légale universelle (Loi 25 · Québec · Canada)
 * ---------------------------------------------------------------------
 * Source unique de vérité pour toutes les informations légales, coordonnées
 * du responsable de la protection des renseignements personnels (RPRP),
 * juridiction, versions des documents et liens légaux du footer.
 *
 * ⚠️ Ce fichier est chargé par le footer, les pages légales, le gestionnaire
 * de consentement et le manifest d'accessibilité. Toute modification doit
 * être répercutée dans /app/backend/routes/legal.py (endpoint config).
 */

export const PROGERE_LEGAL = {
  entity: {
    legalName: "PROGÈRE Devis AI inc.",
    tradeName: "PROGÈRE Devis AI",
    ecosystem: "PROGÈRE",
    poweredBy: "Groupe MJR Construction Inc.",
    website: "https://progere.ca",
    supportEmail: "support@progere.ca",
    salesEmail: "ventes@progere.ca",
  },
  address: {
    line1: "Québec (Québec), Canada",
    country: "Canada",
    province: "Québec",
    jurisdiction: "District judiciaire de Québec",
  },
  // Responsable de la protection des renseignements personnels (Loi 25 art. 8.1)
  dpo: {
    name: "Responsable de la protection des renseignements personnels — PROGÈRE",
    email: "dpo@progere.ca",
    phone: null,
    postalAddress: "Québec (Québec), Canada",
  },
  regulator: {
    name: "Commission d'accès à l'information du Québec (CAI)",
    url: "https://www.cai.gouv.qc.ca/",
    complaintUrl: "https://www.cai.gouv.qc.ca/plaintes-signalements",
  },
  versions: {
    terms: "1.0",
    privacy: "1.0",
    cookies: "1.0",
    accessibility: "1.0",
    lastUpdated: "2026-02-13",
  },
  // Loi 25 art. 3.5 — Conservation des registres
  retention: {
    privacyIncidentsYears: 5,
    accountingRecordsYears: 6, // Impôt fédéral
    consentLogYears: 3,
    accountDeletionDelayDays: 30,
  },
  // Catégories de témoins (Loi 25 art. 8.1 — consentement explicite)
  cookieCategories: [
    {
      id: "essential",
      label: { fr: "Essentiels", en: "Essential" },
      required: true,
      description: {
        fr: "Nécessaires au fonctionnement de l'application (authentification, session, préférence de langue). Ne peuvent pas être désactivés.",
        en: "Required for the application to function (authentication, session, language preference). Cannot be disabled.",
      },
    },
    {
      id: "functional",
      label: { fr: "Fonctionnels", en: "Functional" },
      required: false,
      description: {
        fr: "Améliorent votre expérience (préférences d'affichage, thème, dernières recherches).",
        en: "Enhance your experience (display preferences, theme, recent searches).",
      },
    },
    {
      id: "analytics",
      label: { fr: "Analytique anonyme", en: "Anonymous analytics" },
      required: false,
      description: {
        fr: "Mesures agrégées et anonymisées d'utilisation pour améliorer le produit. Aucune donnée personnelle identifiable.",
        en: "Aggregated and anonymized usage metrics to improve the product. No personally identifiable data.",
      },
    },
    {
      id: "marketing",
      label: { fr: "Communications marketing", en: "Marketing communications" },
      required: false,
      description: {
        fr: "Envoi de nouveautés produit et offres commerciales. Vous pouvez vous désabonner à tout moment.",
        en: "Product news and commercial offers. You can unsubscribe at any time.",
      },
    },
  ],
  // Liens du footer universel — ordre et étiquettes normatifs PROGÈRE
  footerLinks: [
    { to: "/confidentialite", labelFr: "Confidentialité (Loi 25)", labelEn: "Privacy (Law 25)", testId: "footer-privacy" },
    { to: "/conditions", labelFr: "Conditions", labelEn: "Terms", testId: "footer-terms" },
    { to: "/temoins", labelFr: "Témoins & consentement", labelEn: "Cookies & consent", testId: "footer-cookies" },
    { to: "/mentions-legales", labelFr: "Mentions légales", labelEn: "Legal notice", testId: "footer-legal" },
    { to: "/accessibilite", labelFr: "Accessibilité", labelEn: "Accessibility", testId: "footer-a11y" },
    { to: "/securite", labelFr: "Sécurité", labelEn: "Security", testId: "footer-security" },
    { to: "/demande-confidentialite", labelFr: "Exercer mes droits", labelEn: "Exercise my rights", testId: "footer-request" },
  ],
  // Sous-traitants (Loi 25 art. 17 — transfert hors Québec)
  subprocessors: [
    { name: "Stripe Inc.", purpose: { fr: "Paiements", en: "Payments" }, location: "États-Unis", compliance: "PCI-DSS Level 1" },
    { name: "Resend Inc.", purpose: { fr: "Envoi de courriels", en: "Transactional email" }, location: "États-Unis", compliance: "SOC 2 Type II" },
    { name: "Anthropic (via Emergent)", purpose: { fr: "IA générative", en: "Generative AI" }, location: "États-Unis", compliance: "SOC 2 Type II" },
    { name: "MongoDB Atlas", purpose: { fr: "Base de données", en: "Database hosting" }, location: "Canada (Montréal)", compliance: "SOC 2 · ISO 27001" },
  ],
  // Types de demande de confidentialité (Loi 25 art. 27-33)
  privacyRequestTypes: [
    { id: "access", labelFr: "Accès à mes renseignements", labelEn: "Access my personal information" },
    { id: "rectification", labelFr: "Rectification", labelEn: "Rectification" },
    { id: "deletion", labelFr: "Suppression / droit à l'oubli", labelEn: "Deletion / right to be forgotten" },
    { id: "portability", labelFr: "Portabilité des données", labelEn: "Data portability" },
    { id: "consent_withdrawal", labelFr: "Retrait du consentement", labelEn: "Withdraw consent" },
    { id: "complaint", labelFr: "Plainte / signalement", labelEn: "Complaint / report" },
  ],
};

/**
 * Vérifie que la configuration légale est complète.
 * Retourne { ok: boolean, missing: string[] } — utilisé au démarrage frontend.
 */
export function validateLegalConfig() {
  const missing = [];
  const cfg = PROGERE_LEGAL;
  if (!cfg.entity?.legalName) missing.push("entity.legalName");
  if (!cfg.dpo?.email) missing.push("dpo.email");
  if (!cfg.address?.province) missing.push("address.province");
  if (!cfg.versions?.terms) missing.push("versions.terms");
  if (!cfg.versions?.privacy) missing.push("versions.privacy");
  if (!Array.isArray(cfg.cookieCategories) || cfg.cookieCategories.length === 0) missing.push("cookieCategories");
  if (!Array.isArray(cfg.footerLinks) || cfg.footerLinks.length === 0) missing.push("footerLinks");
  return { ok: missing.length === 0, missing };
}

export default PROGERE_LEGAL;
